/**
 * Monster collection / shiny helpers (P5 / D38).
 */

import {
	MONSTER_CARD_DROP_RATE,
	SHINY_ENCOUNTER_RATE,
	SHINY_GOLD_MULTIPLIER,
} from "./gamification-constants";
import { hashToUint32 } from "./encounter-seed";
import type { EnemyCoreStats } from "./enemy-stat-profile";

export {
	MONSTER_CARD_DROP_RATE,
	SHINY_ENCOUNTER_RATE,
	SHINY_GOLD_MULTIPLIER,
};

/** Default shiny boost when EnemyType.shinyStatMultiplierJson is null. */
export const DEFAULT_SHINY_STAT_MULTIPLIERS: EnemyCoreStats = {
	attack: 1.25,
	defence: 1.2,
	hitpoints: 1.5,
	manaPoints: 1.25,
	accuracy: 1.15,
	evasion: 1.15,
	speed: 1.1,
	critical: 1.2,
};

export const SEED_MONSTER_CARD_USER_ID = "cml80el9q0000yg8on7kbvc3y";

/** Shared art SoT for all `monster-card-*` ItemTypes. */
export const MONSTER_CARD_ICON_PATH = "/images/items/monster-card.png";

/** Shared art SoT for all `shiny-monster-card-*` ItemTypes. */
export const SHINY_MONSTER_CARD_ICON_PATH =
	"/images/items/shiny-monster-card.png";

export function monsterCardItemSlug(enemySlug: string): string {
	return `monster-card-${enemySlug}`;
}

export function shinyMonsterCardItemSlug(enemySlug: string): string {
	return `shiny-monster-card-${enemySlug}`;
}

/** Client/server fallback when ItemType.icon is missing. */
export function resolveMonsterCardIconPath(slug: string): string | null {
	const s = slug.trim();
	if (s.startsWith("shiny-monster-card-")) {
		return SHINY_MONSTER_CARD_ICON_PATH;
	}
	if (s.startsWith("monster-card-")) {
		return MONSTER_CARD_ICON_PATH;
	}
	return null;
}

/**
 * Deterministic shiny roll from encounter seed (peek ↔ spawn parity).
 */
export function rollShinyEncounter(params: {
	seedKey: string;
	rate?: number;
}): boolean {
	const rate = params.rate ?? SHINY_ENCOUNTER_RATE;
	if (rate <= 0) {
		return false;
	}
	if (rate >= 1) {
		return true;
	}
	const h = hashToUint32(`${params.seedKey}:shiny`);
	const unit = h / 0x1_0000_0000;
	return unit < rate;
}

export function parseShinyStatMultipliers(
	raw: unknown,
): EnemyCoreStats {
	if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
		return { ...DEFAULT_SHINY_STAT_MULTIPLIERS };
	}
	const o = raw as Record<string, unknown>;
	const pick = (key: keyof EnemyCoreStats, fallback: number): number => {
		const v = o[key];
		return typeof v === "number" && Number.isFinite(v) && v > 0
			? v
			: fallback;
	};
	return {
		attack: pick("attack", DEFAULT_SHINY_STAT_MULTIPLIERS.attack),
		defence: pick("defence", DEFAULT_SHINY_STAT_MULTIPLIERS.defence),
		hitpoints: pick("hitpoints", DEFAULT_SHINY_STAT_MULTIPLIERS.hitpoints),
		manaPoints: pick(
			"manaPoints",
			DEFAULT_SHINY_STAT_MULTIPLIERS.manaPoints,
		),
		accuracy: pick("accuracy", DEFAULT_SHINY_STAT_MULTIPLIERS.accuracy),
		evasion: pick("evasion", DEFAULT_SHINY_STAT_MULTIPLIERS.evasion),
		speed: pick("speed", DEFAULT_SHINY_STAT_MULTIPLIERS.speed),
		critical: pick("critical", DEFAULT_SHINY_STAT_MULTIPLIERS.critical),
	};
}

export function applyShinyStatMultipliers(
	stats: EnemyCoreStats,
	multipliers: EnemyCoreStats,
): EnemyCoreStats {
	const scale = (v: number, m: number) => Math.max(1, Math.round(v * m));
	return {
		attack: scale(stats.attack, multipliers.attack),
		defence: scale(stats.defence, multipliers.defence),
		hitpoints: scale(stats.hitpoints, multipliers.hitpoints),
		manaPoints: scale(stats.manaPoints, multipliers.manaPoints),
		accuracy: scale(stats.accuracy, multipliers.accuracy),
		evasion: scale(stats.evasion, multipliers.evasion),
		speed: scale(stats.speed, multipliers.speed),
		critical: scale(stats.critical, multipliers.critical),
	};
}

export function applyShinyGoldMultiplier(baseGold: number): number {
	return Math.max(0, Math.round(baseGold * SHINY_GOLD_MULTIPLIER));
}
